// Admin Quote Builder — sales staff can compute a live 3-tier quote for a
// prospective customer (any destination, dates, pax count) and download it
// as a PDF to send over WhatsApp / email / phone. No booking is created —
// this is a pure pricing preview + PDF export tool.

import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Calculator, Download, Eye, Loader2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Common Indian departure airports — sales staff can also type any 3-letter
// IATA code in the field, this list just accelerates the common cases.
const COMMON_AIRPORTS = [
  { code: "DEL", name: "Delhi" },
  { code: "BOM", name: "Mumbai" },
  { code: "BLR", name: "Bengaluru" },
  { code: "MAA", name: "Chennai" },
  { code: "HYD", name: "Hyderabad" },
  { code: "CCU", name: "Kolkata" },
  { code: "COK", name: "Kochi" },
  { code: "PNQ", name: "Pune" },
  { code: "AMD", name: "Ahmedabad" },
  { code: "GOI", name: "Goa" },
];

const fmtINR = (n) =>
  n && n > 0 ? `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : "—";

// Yields a comma-formatted trip-length integer safely across the calendar.
function daysBetween(a, b) {
  if (!a || !b) return 0;
  const start = new Date(a);
  const end = new Date(b);
  if (isNaN(start) || isNaN(end)) return 0;
  return Math.max(0, Math.round((end - start) / (1000 * 60 * 60 * 24)));
}

export default function QuoteBuilderTab() {
  const [destinations, setDestinations] = useState([]);
  const [config, setConfig] = useState({
    destination_id: "",
    start_date: "",
    end_date: "",
    adults: 2,
    children: 0,
    infants: 0,
    departure_airport: "BLR",
    include_flight: true,
    include_hotel: true,
    include_transfer: true,
    include_itinerary: false,
    itinerary_days: null,
    // Free-form admin note surfaced on the PDF as a "Special requests"
    // callout. Doesn't affect pricing — purely informational.
    special_requests: "",
  });
  const [quote, setQuote] = useState(null);
  const [computing, setComputing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // Iter 55 — lightweight in-browser PDF preview modal. `previewUrl` holds
  // a blob-URL that the modal's <iframe> streams the PDF from; `previewing`
  // is the loading flag for the "Preview PDF" button (server call in flight);
  // `previewName` is the filename we'd use if the user clicks "Download"
  // from inside the modal.
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewName, setPreviewName] = useState("");
  const [previewing, setPreviewing] = useState(false);

  // Free the blob URL when the modal closes / component unmounts so the
  // browser doesn't leak memory across repeated previews.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/destinations`);
        setDestinations(res.data || []);
      } catch (err) {
        toast.error("Could not load destinations: " + (err.response?.data?.detail || err.message));
      }
    })();
  }, []);

  const nights = useMemo(() => daysBetween(config.start_date, config.end_date), [config.start_date, config.end_date]);
  const maxItinDays = Math.max(0, nights - 1);

  const patch = (k, v) => setConfig((prev) => ({ ...prev, [k]: v }));

  const configReady =
    config.destination_id &&
    config.start_date &&
    config.end_date &&
    nights > 0 &&
    Number(config.adults) + Number(config.children) + Number(config.infants) > 0;

  const compute = async () => {
    if (!configReady) {
      toast.error("Fill in destination, dates and at least one traveller");
      return;
    }
    setComputing(true);
    try {
      const res = await axios.post(`${API}/admin/quote/preview`, {
        ...config,
        adults: Number(config.adults) || 0,
        children: Number(config.children) || 0,
        infants: Number(config.infants) || 0,
        itinerary_days: config.include_itinerary && config.itinerary_days != null
          ? Number(config.itinerary_days)
          : null,
      });
      setQuote(res.data);
      toast.success("Quote computed");
    } catch (err) {
      toast.error("Could not compute quote: " + (err.response?.data?.detail || err.message));
    } finally {
      setComputing(false);
    }
  };

  // Shared helper — fetches the PDF as a Blob + surfaces the same detailed
  // error message the `download()` handler uses. Returns `{ blob, fname }`
  // on success or throws on error (caller shows the toast).
  const fetchQuotePdf = async () => {
    const res = await axios.post(`${API}/admin/quote/pdf`, {
      ...config,
      adults: Number(config.adults) || 0,
      children: Number(config.children) || 0,
      infants: Number(config.infants) || 0,
      itinerary_days: config.include_itinerary && config.itinerary_days != null
        ? Number(config.itinerary_days)
        : null,
    }, { responseType: "blob" });
    const cd = res.headers["content-disposition"] || "";
    const match = cd.match(/filename="?([^"]+)"?/);
    const fname = match ? match[1] : `Travelsate-Quote-${config.destination_id}-${config.start_date}.pdf`;
    return { blob: new Blob([res.data], { type: "application/pdf" }), fname };
  };

  // Extracts the FastAPI `detail` from a Blob error response so the toast
  // shows the actual server error, not the generic axios message.
  const extractBlobError = async (err) => {
    let detail = err.message;
    const blob = err.response?.data;
    if (blob && typeof blob.text === "function") {
      try {
        const raw = await blob.text();
        const parsed = JSON.parse(raw);
        if (parsed?.detail) detail = parsed.detail;
      } catch {
        // Not JSON — keep axios' error message.
      }
    } else if (err.response?.data?.detail) {
      detail = err.response.data.detail;
    }
    return detail;
  };

  const preview = async () => {
    if (!configReady) {
      toast.error("Fill in destination, dates and at least one traveller");
      return;
    }
    setPreviewing(true);
    try {
      const { blob, fname } = await fetchQuotePdf();
      // Free any previous preview blob before we overwrite the state so the
      // browser doesn't accumulate detached PDFs in memory.
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
      setPreviewName(fname);
    } catch (err) {
      const detail = await extractBlobError(err);
      toast.error("Could not preview PDF: " + detail);
    } finally {
      setPreviewing(false);
    }
  };

  const download = async () => {
    if (!configReady) {
      toast.error("Fill in destination, dates and at least one traveller");
      return;
    }
    setDownloading(true);
    try {
      const { blob, fname } = await fetchQuotePdf();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`PDF downloaded — ${fname}`);
    } catch (err) {
      const detail = await extractBlobError(err);
      toast.error("Could not download PDF: " + detail);
    } finally {
      setDownloading(false);
    }
  };

  // Called from the "Download" button INSIDE the preview modal. Reuses the
  // already-fetched blob so we don't hit the server a second time.
  const downloadFromPreview = () => {
    if (!previewUrl) return;
    const a = document.createElement("a");
    a.href = previewUrl;
    a.download = previewName || `Travelsate-Quote.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success(`PDF downloaded — ${previewName}`);
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewName("");
  };

  return (
    <div data-testid="admin-quote-builder" className="space-y-6">
      <div>
        <h2 className="text-white text-lg font-semibold">Quote Builder</h2>
        <p className="text-white/50 text-xs mt-1">
          Generate a fully-priced 3-tier package quote for a prospect. Live flight + hotel prices,
          same numbers your customer would see on Step 5. Download as PDF and share.
        </p>
      </div>

      {/* ── Configuration form ───────────────────────────────────────────── */}
      <div className="bg-white/[0.02] border border-white/10 rounded-lg p-5 space-y-5">
        {/* Row 1: Destination + Dates */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-white/70 text-xs">Destination</Label>
            <select
              value={config.destination_id}
              onChange={(e) => patch("destination_id", e.target.value)}
              className="w-full mt-1 bg-black/30 border border-white/10 text-white text-sm rounded px-2 py-2 focus:outline-none focus:border-[#D4AF37]"
              data-testid="quote-destination"
            >
              <option value="">Select a destination…</option>
              <optgroup label="Domestic">
                {destinations.filter((d) => (d.type || "").toLowerCase() === "domestic").map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </optgroup>
              <optgroup label="International">
                {destinations.filter((d) => (d.type || "").toLowerCase() === "international").map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </optgroup>
            </select>
          </div>
          <div>
            <Label className="text-white/70 text-xs">Start date</Label>
            <Input
              type="date"
              value={config.start_date}
              onChange={(e) => patch("start_date", e.target.value)}
              className="mt-1 bg-black/30 border-white/10 text-white text-sm"
              data-testid="quote-start-date"
            />
          </div>
          <div>
            <Label className="text-white/70 text-xs">
              End date {nights > 0 && <span className="text-white/40">· {nights} nights</span>}
            </Label>
            <Input
              type="date"
              value={config.end_date}
              onChange={(e) => patch("end_date", e.target.value)}
              className="mt-1 bg-black/30 border-white/10 text-white text-sm"
              data-testid="quote-end-date"
            />
          </div>
        </div>

        {/* Row 2: Departure airport + pax */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-white/70 text-xs">Departure airport (IATA)</Label>
            <div className="flex gap-2 mt-1">
              <Input
                type="text"
                value={config.departure_airport}
                onChange={(e) => patch("departure_airport", e.target.value.toUpperCase().slice(0, 3))}
                placeholder="BLR"
                maxLength={3}
                className="bg-black/30 border-white/10 text-white text-sm font-mono tracking-wider uppercase w-24"
                data-testid="quote-departure-airport"
              />
              <select
                value=""
                onChange={(e) => e.target.value && patch("departure_airport", e.target.value)}
                className="flex-1 bg-black/30 border border-white/10 text-white/70 text-xs rounded px-2 focus:outline-none focus:border-[#D4AF37]"
                data-testid="quote-departure-picker"
              >
                <option value="">Pick from list…</option>
                {COMMON_AIRPORTS.map((a) => (
                  <option key={a.code} value={a.code}>{a.code} · {a.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <Label className="text-white/70 text-xs">Adults</Label>
            <Input
              type="number" min={0} max={20}
              value={config.adults}
              onChange={(e) => patch("adults", e.target.value.replace(/[^0-9]/g, ""))}
              className="mt-1 bg-black/30 border-white/10 text-white text-sm"
              data-testid="quote-adults"
            />
          </div>
          <div>
            <Label className="text-white/70 text-xs">Children</Label>
            <Input
              type="number" min={0} max={20}
              value={config.children}
              onChange={(e) => patch("children", e.target.value.replace(/[^0-9]/g, ""))}
              className="mt-1 bg-black/30 border-white/10 text-white text-sm"
              data-testid="quote-children"
            />
          </div>
          <div>
            <Label className="text-white/70 text-xs">Infants</Label>
            <Input
              type="number" min={0} max={10}
              value={config.infants}
              onChange={(e) => patch("infants", e.target.value.replace(/[^0-9]/g, ""))}
              className="mt-1 bg-black/30 border-white/10 text-white text-sm"
              data-testid="quote-infants"
            />
          </div>
        </div>

        {/* Row 3: Include/exclude toggles */}
        <div>
          <Label className="text-white/70 text-xs">Include in quote</Label>
          <div className="flex flex-wrap gap-3 mt-2" data-testid="quote-includes">
            {[
              { k: "include_flight", label: "Flight" },
              { k: "include_hotel", label: "Hotel" },
              { k: "include_transfer", label: "Transfers" },
              { k: "include_itinerary", label: "Itinerary" },
            ].map((opt) => (
              <label
                key={opt.k}
                className={`cursor-pointer text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  config[opt.k]
                    ? "bg-[#D4AF37]/15 border-[#D4AF37] text-[#D4AF37]"
                    : "bg-white/[0.02] border-white/10 text-white/50 hover:border-white/25"
                }`}
              >
                <input
                  type="checkbox"
                  checked={config[opt.k]}
                  onChange={(e) => patch(opt.k, e.target.checked)}
                  className="sr-only"
                  data-testid={`quote-toggle-${opt.k}`}
                />
                {opt.label}
              </label>
            ))}
          </div>
          {config.include_itinerary && maxItinDays > 0 && (
            <div className="mt-3 flex items-center gap-2">
              <Label className="text-white/60 text-xs">Itinerary days (max {maxItinDays}):</Label>
              <Input
                type="number" min={0} max={maxItinDays}
                value={config.itinerary_days ?? maxItinDays}
                onChange={(e) => patch("itinerary_days", Math.max(0, Math.min(maxItinDays, Number(e.target.value) || 0)))}
                className="w-20 bg-black/30 border-white/10 text-white text-sm"
                data-testid="quote-itinerary-days"
              />
            </div>
          )}
        </div>

        {/* Special requests — free-form note that renders as a highlighted
            callout in the PDF. Doesn't affect pricing. */}
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label className="text-white/70 text-xs">Special requests / notes (optional)</Label>
            <span className="text-white/40 text-[10px]" data-testid="quote-special-requests-count">
              {(config.special_requests || "").length}/2000
            </span>
          </div>
          <textarea
            value={config.special_requests}
            onChange={(e) => patch("special_requests", e.target.value.slice(0, 2000))}
            placeholder="e.g. Wheelchair-accessible transfer · Halal meals only · Anniversary — set up cake in room on arrival · Kids need cribs on 2 hotel nights"
            rows={3}
            className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]/50 resize-y"
            data-testid="quote-special-requests"
          />
          <p className="text-white/40 text-[11px] leading-snug">
            Appears as a gold-bordered callout in the PDF, right below the pricing table.
            Purely informational — never changes the price.
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <Button
            onClick={compute}
            disabled={computing || !configReady}
            className="bg-white/10 text-white hover:bg-white/15 border border-white/15 disabled:opacity-50"
            data-testid="quote-compute-btn"
          >
            {computing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calculator className="w-4 h-4 mr-2" />}
            {computing ? "Computing…" : "Compute preview"}
          </Button>
          <Button
            onClick={preview}
            disabled={previewing || !configReady}
            className="bg-white/10 text-white hover:bg-white/15 border border-white/15 disabled:opacity-50"
            data-testid="quote-preview-btn"
          >
            {previewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
            {previewing ? "Building preview…" : "Preview PDF"}
          </Button>
          <Button
            onClick={download}
            disabled={downloading || !configReady}
            className="bg-[#D4AF37] text-black hover:bg-[#E5C468] disabled:opacity-50"
            data-testid="quote-download-btn"
          >
            {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            {downloading ? "Building PDF…" : "Download PDF"}
          </Button>
        </div>
      </div>

      {/* ── Preview table ────────────────────────────────────────────────── */}
      {quote && (
        <div className="bg-white/[0.02] border border-white/10 rounded-lg overflow-hidden" data-testid="quote-preview">
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <div>
              <p className="text-white text-sm font-semibold">{quote.destination.name}</p>
              <p className="text-white/50 text-xs mt-0.5">
                {quote.config.start_date} → {quote.config.end_date} · {quote.config.nights} nights ·
                {" "}{quote.config.adults}A + {quote.config.children}C + {quote.config.infants}I ·
                {" "}from {quote.config.departure_airport}
              </p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-white/40 text-[10px] uppercase tracking-wider bg-white/[0.02]">
                <tr>
                  <th className="text-left p-3">Line item</th>
                  <th className="text-right p-3">Luxury</th>
                  <th className="text-right p-3">Premium</th>
                  <th className="text-right p-3">Basic</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { label: "Flight", key: "flight_cost", flag: "include_flight" },
                  { label: "Hotel", key: "hotel_cost", flag: "include_hotel" },
                  { label: "Transfers", key: "transfer_cost", flag: "include_transfer" },
                  { label: "Itinerary", key: "itinerary_cost", flag: "include_itinerary" },
                ].map((row) => (
                  <tr key={row.key} className="border-t border-white/5">
                    <td className="p-3 text-white/70">
                      {row.label}
                      {!quote.config[row.flag] && <span className="text-white/25 text-[10px] ml-2">(excluded)</span>}
                    </td>
                    <td className="p-3 text-right text-white/80" data-testid={`preview-luxury-${row.key}`}>
                      {fmtINR(quote.tiers.luxury[row.key])}
                    </td>
                    <td className="p-3 text-right text-white/80" data-testid={`preview-premium-${row.key}`}>
                      {fmtINR(quote.tiers.premium[row.key])}
                    </td>
                    <td className="p-3 text-right text-white/80" data-testid={`preview-basic-${row.key}`}>
                      {fmtINR(quote.tiers.basic[row.key])}
                    </td>
                  </tr>
                ))}
                <tr className="border-t border-white/10 bg-white/[0.02]">
                  <td className="p-3 text-white/60">Subtotal</td>
                  <td className="p-3 text-right text-white/80">{fmtINR(quote.tiers.luxury.subtotal)}</td>
                  <td className="p-3 text-right text-white/80">{fmtINR(quote.tiers.premium.subtotal)}</td>
                  <td className="p-3 text-right text-white/80">{fmtINR(quote.tiers.basic.subtotal)}</td>
                </tr>
                <tr className="bg-white/[0.02]">
                  <td className="p-3 text-white/60">+ 18% IGST</td>
                  <td className="p-3 text-right text-white/60">{fmtINR(quote.tiers.luxury.tax)}</td>
                  <td className="p-3 text-right text-white/60">{fmtINR(quote.tiers.premium.tax)}</td>
                  <td className="p-3 text-right text-white/60">{fmtINR(quote.tiers.basic.tax)}</td>
                </tr>
                <tr className="border-t border-[#D4AF37]/50">
                  <td className="p-3 text-[#D4AF37] font-semibold">Total</td>
                  <td className="p-3 text-right text-[#D4AF37] font-semibold" data-testid="preview-luxury-total">
                    {fmtINR(quote.tiers.luxury.total)}
                  </td>
                  <td className="p-3 text-right text-[#D4AF37] font-semibold" data-testid="preview-premium-total">
                    {fmtINR(quote.tiers.premium.total)}
                  </td>
                  <td className="p-3 text-right text-[#D4AF37] font-semibold" data-testid="preview-basic-total">
                    {fmtINR(quote.tiers.basic.total)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── PDF Preview Modal ────────────────────────────────────────────
          Lightweight iframe-based preview so sales staff can spot-check
          the quote layout without saving a file to disk on every attempt.
          The user can either close the modal or download the exact PDF
          they just previewed (no second server round-trip). */}
      {previewUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closePreview}
          data-testid="quote-preview-modal"
        >
          <div
            className="bg-[#0b0b0b] border border-white/10 rounded-lg w-full max-w-5xl h-[90vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div className="min-w-0">
                <p className="text-white text-sm font-semibold truncate">Quote preview</p>
                <p className="text-white/40 text-xs truncate" data-testid="quote-preview-filename">
                  {previewName}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  onClick={downloadFromPreview}
                  size="sm"
                  className="bg-[#D4AF37] text-black hover:bg-[#E5C468]"
                  data-testid="quote-preview-download-btn"
                >
                  <Download className="w-4 h-4 mr-1.5" />
                  Download
                </Button>
                <button
                  onClick={closePreview}
                  className="w-8 h-8 rounded-md flex items-center justify-center text-white/60 hover:text-white hover:bg-white/5 transition-colors"
                  aria-label="Close preview"
                  data-testid="quote-preview-close-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <iframe
              title="Quote PDF preview"
              src={previewUrl}
              className="flex-1 w-full bg-white"
              data-testid="quote-preview-iframe"
            />
          </div>
        </div>
      )}
    </div>
  );
}
